import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Instagram, Mail, PhoneCall, X } from 'lucide-react';
import ContactForm from './ContactForm';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-primary/95 backdrop-blur-sm shadow-lg py-2' : 'bg-transparent py-5'
      }`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 100, damping: 15 }}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center">
          <motion.div 
            className="flex items-center"
            whileHover={{ scale: 1.03 }}
            transition={{ type: 'spring', stiffness: 400, damping: 10 }}
          >
            <motion.img 
              src="/logo.png" 
              alt="Logo" 
              width={isScrolled ? 150 : 200} 
              height={isScrolled ? 150 : 200} 
              className="mr-2 transition-all duration-300"
              initial={{ rotate: -5 }}
              animate={{ rotate: 0 }}
              transition={{ duration: 0.5 }}
            />
          </motion.div>

          <motion.nav className="hidden md:flex items-center space-x-6">
            {[
              { name: 'Início', href: '#' },
              { name: 'Serviços', href: '#services' },
              { name: 'FAQ', href: '#faq' },
              { name: 'Sobre', href: '#about' },
            ].map((item, i) => (
              <motion.a 
                key={item.name}
                href={item.href}
                className="text-white/80 hover:text-white font-medium text-sm transition-colors"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 + 0.2 }}
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
              >
                {item.name}
              </motion.a>
            ))}

            {/* Redes Sociais */}
            <div className="flex items-center space-x-3 border-l border-white/20 pl-5 ml-2">
              {[
                { icon: <Instagram size={18} />, href: 'https://www.instagram.com/lidianealmeidaadv?igsh=Z3h6bjhuN2drdmh3&utm_source=qr', ariaLabel: 'Instagram' },
                { icon: <PhoneCall size={18} />, href: 'https://wa.me/5581993507689', ariaLabel: 'WhatsApp' },
                { icon: <Mail size={18} />, href: 'mailto:contato@lidianealmeidaadv.com', ariaLabel: 'E-mail' },
              ].map((social, i) => (
                <motion.a
                  key={social.ariaLabel}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.ariaLabel}
                  className="text-white/70 hover:text-white p-1.5 bg-white/10 hover:bg-accent/20 rounded-full transition-all duration-300"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: i * 0.1 + 0.6 }}
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.nav>

          <motion.button 
  className="bg-gradient-to-r from-accent to-accent/80 hover:from-accent/90 hover:to-accent/70 text-white font-semibold py-2.5 px-6 rounded-full transition-all duration-300 hidden md:flex items-center shadow-lg shadow-accent/20 group"
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.98 }}
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ delay: 0.6 }}
  onClick={() => {
    const message = "Encontrei seu contato pelo site e gostaria de solicitar algumas informações!";
    window.open(`https://wa.me/5581993507689?text=${encodeURIComponent(message)}`, '_blank');
  }}
>
  <MessageSquare size={16} className="mr-2 group-hover:animate-pulse" />
  Contato
</motion.button>

          <motion.button 
            className="md:hidden bg-accent/90 text-white p-2 rounded-md"
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X size={24} />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            )}
          </motion.button>
        </div>
      </div>

      {/* Menu Mobile */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            className="absolute top-full left-0 w-full bg-primary/95 backdrop-blur-sm shadow-lg py-4 md:hidden"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container mx-auto px-4">
              <nav className="flex flex-col space-y-4">
                {[
                  { name: 'Início', href: '#' },
                  { name: 'Serviços', href: '#services' },
                  { name: 'FAQ', href: '#faq' },
                  { name: 'Sobre', href: '#about' },
                ].map((item, i) => (
                  <motion.a 
                    key={item.name}
                    href={item.href}
                    className="text-white/80 hover:text-white font-medium text-base transition-colors px-2 py-1"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </motion.a>
                ))}
                
                {/* Botão de Contato Mobile */}
                <motion.button 
                  className="bg-gradient-to-r from-accent to-accent/80 text-white font-semibold py-2.5 px-6 rounded-full transition-all duration-300 flex items-center shadow-lg shadow-accent/20 w-full justify-center mt-2"
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Mensagem predefinida para o WhatsApp
                    const message = "Olá, Dra. Lidiane!\nEncontrei seu contato pelo site e gostaria de solicitar algumas informações!";
                    
                    // Abrir WhatsApp com a mensagem
                    window.open(`https://wa.me/5581993507689?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                >
                  <MessageSquare size={16} className="mr-2" />
                  Contato
                </motion.button>
                
                {/* Redes Sociais Mobile */}
                <div className="flex items-center space-x-3 pt-3 border-t border-white/20 mt-2">
                  <span className="text-white/60 text-sm">Redes Sociais:</span>
                  {[
                    { icon: <Instagram size={18} />, href: 'https://www.instagram.com/lidianealmeidaadv?igsh=Z3h6bjhuN2drdmh3&utm_source=qr', ariaLabel: 'Instagram' },
                    { icon: <PhoneCall size={18} />, href: 'https://wa.me/5581993507689', ariaLabel: 'WhatsApp' },
                    { icon: <Mail size={18} />, href: 'mailto:contato@lidianealmeidaadv.com', ariaLabel: 'E-mail' },
                  ].map((social, i) => (
                    <motion.a
                      key={social.ariaLabel}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.ariaLabel}
                      className="text-white/70 hover:text-white p-1.5 bg-white/10 hover:bg-accent/20 rounded-full transition-all duration-300"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: i * 0.1 + 0.5 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {isFormOpen && <ContactForm onClose={() => setIsFormOpen(false)} />}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;